import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMountain, FiCamera, FiCompass, FiSun, FiDroplet, FiMapPin } = FiIcons;

const Experience = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const experiences = [
    {
      icon: FiMountain,
      title: 'Gongala Mountain Expedition',
      description: 'Hike to the 15th highest peak in Sri Lanka through misty pygmy forests',
      details: ['2-3 hour hike', 'Best at sunrise', 'Endemic wildlife', 'Panoramic views'],
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=400&fit=crop'
    },
    {
      icon: FiDroplet,
      title: 'Patna Sliding Rock Adventure',
      description: 'Natural water slide over smooth rock formations into crystal pools',
      details: ['Family friendly', '15-minute walk', 'Natural pools', 'Perfect for photos'],
      image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=600&h=400&fit=crop'
    },
    {
      icon: FiCompass,
      title: 'Sinharaja Forest Exploration',
      description: 'UNESCO World Heritage rainforest with rare birds and pristine wilderness',
      details: ['Guided tours available', 'Blue Magpie spotting', 'Morning Side access', '30 minutes away'],
      image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=600&h=400&fit=crop'
    },
    {
      icon: FiSun,
      title: 'Tea Estate Walks',
      description: 'Stroll through our 10-acre private estate and learn about tea cultivation',
      details: ['Self-guided trails', 'Tea plucking experience', 'Sunset viewpoints', 'Photography spots'],
      image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=600&h=400&fit=crop'
    }
  ];

  const activities = [
    { icon: FiCamera, title: 'Photography Tours', desc: 'Capture the misty mountains and tea landscapes' },
    { icon: FiCompass, title: 'Nature Walks', desc: 'Explore diverse ecosystems and wildlife' },
    { icon: FiMountain, title: 'Mountain Climbing', desc: 'Challenge yourself on nearby peaks' },
    { icon: FiSun, title: 'Sunrise Viewing', desc: 'Watch the sun rise over the Gongala range' },
    { icon: FiMapPin, title: 'Cultural Sites', desc: 'Visit ancient temples and local villages' },
    { icon: FiDroplet, title: 'Waterfall Hunting', desc: 'Discover hidden cascades in the forest' },
  ];

  return (
    <section id="experience" className="py-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white font-serif mb-6">
            Unforgettable Experiences
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            From misty mountain hikes to natural water slides, discover the hidden wonders 
            of Ullinduwawa and create memories that will last a lifetime.
          </p>
        </motion.div>

        {/* Main Experiences */}
        <div className="grid lg:grid-cols-2 gap-8 mb-20">
          {experiences.map((experience, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 + index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              className="glass-card rounded-2xl overflow-hidden"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={experience.image}
                  alt={experience.title}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute top-4 left-4">
                  <div className="w-12 h-12 glass-morphism rounded-full flex items-center justify-center">
                    <SafeIcon icon={experience.icon} className="w-6 h-6 text-emerald-300" />
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3 font-serif">
                  {experience.title}
                </h3>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  {experience.description}
                </p>
                
                <div className="grid grid-cols-2 gap-2">
                  {experience.details.map((detail, detailIndex) => (
                    <div key={detailIndex} className="flex items-center text-sm text-gray-400">
                      <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full mr-2"></div>
                      {detail}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Activities */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <h3 className="text-3xl font-bold text-white text-center mb-12 font-serif">
            More Adventures Await
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activities.map((activity, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05, y: -5 }}
                className="glass-card p-6 rounded-xl"
              >
                <div className="w-14 h-14 mb-4 glass-morphism rounded-full flex items-center justify-center">
                  <SafeIcon icon={activity.icon} className="w-7 h-7 text-emerald-300" />
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">{activity.title}</h4>
                <p className="text-gray-300 text-sm">{activity.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Weather & Best Times */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-20"
        >
          <div className="glass-card p-8 rounded-2xl">
            <h3 className="text-3xl font-bold text-white text-center mb-8 font-serif">
              Perfect Climate Year-Round
            </h3>
            
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="w-16 h-16 mx-auto mb-4 glass-morphism rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiSun} className="w-8 h-8 text-yellow-400" />
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">Dry Season</h4>
                <p className="text-gray-300 text-sm mb-2">December - April</p>
                <p className="text-gray-400 text-xs">Clear skies, perfect for hiking and photography</p>
              </div>
              
              <div>
                <div className="w-16 h-16 mx-auto mb-4 glass-morphism rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiDroplet} className="w-8 h-8 text-blue-400" />
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">Monsoon Season</h4>
                <p className="text-gray-300 text-sm mb-2">May - November</p>
                <p className="text-gray-400 text-xs">Lush greenery, dramatic waterfalls, cozy indoor time</p>
              </div>
              
              <div>
                <div className="w-16 h-16 mx-auto mb-4 glass-morphism rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiMountain} className="w-8 h-8 text-emerald-400" />
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">Year-Round</h4>
                <p className="text-gray-300 text-sm mb-2">16°C - 22°C</p>
                <p className="text-gray-400 text-xs">Cool mountain climate, no need for AC</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-16 text-center"
        >
          <div className="glass-card p-8 rounded-2xl">
            <h3 className="text-3xl font-bold text-white mb-4 font-serif">
              Start Your Adventure
            </h3>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Let us help you plan the perfect itinerary for your stay. From gentle nature walks 
              to challenging mountain hikes, we'll customize your experience.
            </p>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="glass-button px-8 py-4 rounded-full text-lg font-semibold pulse-glow"
            >
              Plan Your Experience
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;